import { Container } from 'components/helper';
import React, { useState, useEffect, useMemo } from 'react';
import {
  Button, Box, Typography, Tab, Tabs,
} from '@mui/material';
import { Upload, FilterList } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { watchCollection, getCollectionDocs } from '../api/document-api/DocumentAPI';
import { useAdmin, useRoles } from '../api/user-api/UserAPI';
import { QueryType } from '../api/types/FirebaseTypes';
import { VendorStatementsUploadModal } from './VendorStatementsUploadModal';
import { VendorStatementJobsTable } from './vendor-statements-table/VendorStatementJobsTable';
import { VendorStatementModal } from './job-modal/VendorStatementModal';
import { VendorStatementDoc } from './types';
import { VendorStatementsFilters } from './VendorStatementsFilters';
import { AssociationOption } from './job-modal/types';

// Tab values
enum TabValue {
  Pending = 'Pending Jobs',
  Completed = 'Completed Jobs',
}

/**
 * Filters the jobs based on the selected tab and date range
 * @param allJobs - All jobs to filter
 * @param selectedTab - The selected tab
 * @param startDate - The start date
 * @param endDate - The end date
 * @returns The filtered jobs
 */
const filterJobs = (allJobs: VendorStatementDoc[], selectedTab: TabValue, startDate: string, endDate: string) => {
  let jobs = [...allJobs];

  // Filter by status first
  if (selectedTab === TabValue.Pending) {
    // Pending jobs - all statuses except job-completed
    jobs = jobs.filter((job) => job.status !== 'job-completed');
  }
  if (selectedTab === TabValue.Completed) {
    // Completed jobs - only job-completed status
    jobs = jobs.filter((job) => job.status === 'job-completed');
  }

  // Filter by date range if dates are provided
  if (startDate || endDate) {
    jobs = jobs.filter((job) => {
      const jobDate = new Date(job.createdAt);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      if (start && end) return jobDate >= start && jobDate <= end;
      if (start) return jobDate >= start;
      if (end) return jobDate <= end;

      return true;
    });
  }

  return jobs;
};

/**
 * VendorStatements page. Used to create, manage and view vendor statement jobs.
 */
const VendorStatementsPage = function () {
  const isAdmin = useAdmin();
  const userRoles = useRoles();
  const { enqueueSnackbar } = useSnackbar();

  const [allJobs, setAllJobs] = useState<VendorStatementDoc[]>([]);
  const [selectedJob, setSelectedJob] = useState<VendorStatementDoc | null>(null);
  const [selectedTab, setSelectedTab] = useState<TabValue>(TabValue.Pending);

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  const [users, setUsers] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);

  const [properties, setProperties] = useState<AssociationOption[]>([]);
  const [units, setUnits] = useState<AssociationOption[]>([]);
  const [vendors, setVendors] = useState<AssociationOption[]>([]);

  // Filter jobs based on selected tab and date range
  const filteredJobs = useMemo(
    () => filterJobs(allJobs, selectedTab, startDate, endDate),
    [allJobs, selectedTab, startDate, endDate],
  );

  // Fetch all users for name mapping
  useEffect(() => {
    getCollectionDocs('users')
      .then((docs) => {
        const userMap: Record<string, string> = {};
        docs.forEach((doc) => {
          if (doc.pdocId && doc.displayName) {
            userMap[doc.pdocId] = doc.displayName;
          }
        });
        setUsers(userMap);
      })
      .catch(() => {
        enqueueSnackbar('Error fetching users', { variant: 'error' });
      });
  }, []);

  // Watch all vendor statement jobs
  useEffect(() => {
    const queries: QueryType[] = [];

    const unsubscribe = watchCollection(
      setIsLoading,
      (data: VendorStatementDoc[]) => {
        // exclude deleted jobs
        const jobs = data.filter((job) => !job.deletedAt);
        setAllJobs(jobs);
      },
      'vendor-statements',
      queries,
      { field: 'createdAt', direction: 'desc' },
    );

    return () => unsubscribe();
  }, []);

  // Load properties, units, and vendors options
  useEffect(() => {
    const loadData = async () => {
      try {
        const [propertiesData, vendorsData] = await Promise.all([
          getCollectionDocs('engine/propertyUnit/associations'),
          getCollectionDocs('engine/payee/associations'),
        ]);

        const propertiesOptions = propertiesData.filter((item) => item.dependentOf === null);
        const unitsOptions = propertiesData.filter((item) => item.dependentOf !== null);

        setProperties(propertiesOptions as AssociationOption[]);
        setUnits(unitsOptions as AssociationOption[]);
        setVendors(vendorsData as AssociationOption[]);
      } catch (error) {
        enqueueSnackbar('Error loading data', { variant: 'error' });
      }
    };

    loadData();
  }, []);

  // User must have vendor-statements role or be admin to access this page
  if (!userRoles.includes('vendor-statements') && !isAdmin) {
    return (
      <Container>
        <div>You do not have permission to access this page</div>
      </Container>
    );
  }

  return (
    <Container>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Vendor Statements
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Manage and view vendor statement documents
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Upload Vendor Statements Button */}
          <Button
            variant="contained"
            startIcon={<Upload />}
            onClick={() => { setUploadModalOpen(true); }}
            sx={{
              backgroundColor: '#4682B4',
              '&:hover': {
                backgroundColor: '#70A1B4',
              },
            }}
          >
            Upload Vendor Statements
          </Button>
          {/* Show/Hide Filters Button */}
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => { setShowFilters(!showFilters); }}
            sx={{
              borderColor: '#4682B4',
              color: '#4682B4',
              '&:hover': {
                borderColor: '#70A1B4',
                backgroundColor: '#E0FFFF',
              },
            }}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </Box>
      </Box>

      {/* Tabs selector */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => { setSelectedTab(newValue); }}
          sx={{
            '& .MuiTab-root': {
              color: '#4682B4',
              '&.Mui-selected': {
                color: '#70A1B4',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#70A1B4',
            },
          }}
        >
          <Tab label={TabValue.Pending} value={TabValue.Pending} />
          <Tab label={TabValue.Completed} value={TabValue.Completed} />
        </Tabs>
      </Box>

      {/* Date filters for vendor statements */}
      {showFilters && (
      <VendorStatementsFilters
        startDate={startDate}
        endDate={endDate}
        setStartDate={(date) => { setStartDate(date); }}
        setEndDate={(date) => { setEndDate(date); }}
        filteredJobs={filteredJobs}
      />
      )}

      {/* Vendor statements table */}
      <VendorStatementJobsTable
        jobs={filteredJobs}
        title={selectedTab}
        isLoading={isLoading}
        users={users}
        onJobClick={(job: VendorStatementDoc) => {
          setSelectedJob(job);
          setModalOpen(true);
        }}
      />

      {/* Upload vendor statements modal - where user uploads vendor statements */}
      {uploadModalOpen && (
      <VendorStatementsUploadModal
        open={uploadModalOpen}
        onClose={() => { setUploadModalOpen(false); }}
      />
      )}

      {/* Vendor statement job modal - where user works on the job */}
      {modalOpen && (
      <VendorStatementModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedJob(null);
        }}
        job={selectedJob}
        viewOnly={selectedJob?.status === 'job-completed'}
        properties={properties}
        units={units}
        vendors={vendors}
      />
      )}
    </Container>
  );
};

export default VendorStatementsPage;
